import { z } from 'zod';
import {
  emailSchema,
  idSchema,
  nameSchema,
  objectIdSchema,
  optionalStringSchema,
  phoneSchema,
} from '../validation';
import { nationalityPersonSchema } from './nationality.schema';
import { imageWithUploadSchema } from './image.schema';
import { quantitySchema } from '../validation';
import { DriverSchema } from '../model/driver';
import { userTypeUnifiedSchema } from './user-types.schema';

export const membersCountSchema = z.object({
  /** all users types = admins + dispatchers + drivers + employees  */
  all: quantitySchema.min(0),
  /** managers, owners for both carriers & customers */
  admins: quantitySchema.min(0),
  /** users/people responsible for order handling/assigning in both carrier companies & business/customers */
  dispatchers: quantitySchema.min(0),
  /** a driver member within a carrier company org */
  drivers: quantitySchema.min(0),
  /** any other employee user such as accountant/finance/etc for both carriers & customers */
  employees: quantitySchema.min(0),
});
export type MembersCount = z.infer<typeof membersCountSchema>;

const counter = quantitySchema.min(0);
export const counterSchema = z.object({
  orders: counter,
  sales: counter,
  members: counter,
  drivers: counter,
  admins: counter,
  dispatchers: counter,
  contacts: counter,
  customers: counter,
  vehicles: counter,
});

export const memberTypeSchema = z.enum([
  userTypeUnifiedSchema.enum.driver,
  userTypeUnifiedSchema.enum.admin,
  userTypeUnifiedSchema.enum.dispatcher,
  userTypeUnifiedSchema.enum.employee,
]);
/** a member is a user that belongs to an org that can user our app and dashboard such as
 * - `admin`
 * - `driver`
 * - `dispatcher`
 * - `employee`
 */
export type MemberType = z.infer<typeof memberTypeSchema>;

export const diverMemberSchema = z.object({
  /** the full legal name of the driver written in his/her id.
   *
   * Optional — driver creation only requires `name` + `phone` on the
   * parent `Member`. This field is recommended for the Logisti
   * integration so the carrier can match the driver against
   * government records.
   *
   * Uses `optionalStringSchema` so empty strings from the form get
   * coerced to `undefined` instead of failing `nameSchema.min(1)`.
   */
  fullName: optionalStringSchema(nameSchema),
  /** Optional — recommended for Logisti integration. */
  nationality: nationalityPersonSchema.optional(),
  /** Optional — recommended for Logisti integration (iqama / national id). */
  id: optionalStringSchema(idSchema),
  /**
   * Issue number printed on the identity card (رقم إصدار الهوية) — the counter
   * that increments each time the card is reissued.
   *
   * Bayan requires this alongside `id` on every government waybill and it
   * cannot be derived from the identity number, so a driver without it blocks
   * issuance. See {@link Driver.legal.idIssueNumber}.
   */
  idIssueNumber: z.coerce.number().int().positive().optional(),
  /**
   * هوية صاحب العمل — the employer 70… on the iqama, typed or read.
   * Stored as `Driver.legal.employerIdentity`. Optional on create;
   * required for a freelancer to issue.
   */
  employerIdentity: optionalStringSchema(idSchema),
  /** @deprecated Older clients send the employer number here. Stored as `employerIdentity`. */
  registrationId: optionalStringSchema(idSchema),
  /**
   * @deprecated Never stored as `carrierIdentity` (only Bayan acceptance
   * writes that). A 70… sent here is stored as `employerIdentity` when
   * no employer number is given.
   */
  carrierIdentity: optionalStringSchema(idSchema),
  /** Optional — recommended for Logisti integration. */
  idImage: imageWithUploadSchema.optional(),
  /** Optional — recommended for Logisti integration. */
  driverLicenseImage: imageWithUploadSchema.optional(),
});
export type DiverMember = z.infer<typeof diverMemberSchema>;

export const memberSchema = z.object({
  /** the doc _id mainly will be coming from the `User` collection.
   *
   * Only for `driver` members, this will be coming from `Driver` ...
   * this is because not all drivers will actually use the app.
   *
   * Drivers can be added by admin and later on will download the app and user it.
   * When drivers use the app they will have a `userId`.
   */
  _id: objectIdSchema,
  /** the member type:
   * - `admin` = the mangers/owners user for bother carrier company & business customer
   * - `driver` = the driver user that belongs to a carrier company. a freelancer will not be a member of any org.
   * - `dispatcher` = the user/person responsible to handling/assigning orders for bother carrier company & business customer
   * - `employee` = any other employee such as accountant/finance/etc for bother carrier company & business customer
   */
  type: memberTypeSchema,
  /**
   * this will always be coming from `User` collection.
   *
   * It's optional because some members does not have `yet` a user to start using the app such as drivers.
   *
   */
  userId: objectIdSchema.optional(),

  /** name of the member */
  name: nameSchema,

  /** the phone number of the member */
  phone: phoneSchema,

  /** the member email */
  email: emailSchema.optional(),

  /**
   * whether the person behind this member has ever signed in
   * (derived from `User.lastSignIn` on the linked User doc).
   *
   * Once true, the phone number is LOCKED — it is the person's
   * verified sign-in identity, so neither org admins nor the user
   * themselves may change it (backend throws
   * `member_phone_locked_after_sign_in`). Frontends use this to
   * disable the phone field in member edit forms.
   */
  hasLoggedIn: z.boolean().optional(),

  /**
   * Set on a driver whose account another org also uses: that account's
   * own name, which renaming the driver leaves alone unless the admin
   * opts in (`MemberUpdateAPI.renameAccount`).
   */
  sharedAccount: z.object({ name: nameSchema }).optional(),

  /** the driver info if the member type is `driver` */
  driver: diverMemberSchema.optional(),
  /** the full driver data object if the member is a driver */
  //driverFullInfo: DriverSchema.model.optional(),
  driverFullInfo: DriverSchema.model.optional(),
});

export type Member = z.infer<typeof memberSchema>;
